// frontend/src/components/ForgotPassword.js
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { authAPI } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';

const normalizeEmail = (v) => String(v || '').trim().toLowerCase();

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const emailValue = useMemo(() => normalizeEmail(email), [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInfo('');
    setError('');

    const safeEmail = normalizeEmail(email);
    if (!safeEmail) {
      setError('Please enter your email address.');
      return;
    }

    setBusy(true);
    try {
      // Always show a generic success message to avoid user enumeration
      await authAPI.forgotPassword({ email: safeEmail });

      setInfo(
        'If that account exists, we’ve sent a password reset link to the email address.'
      );
      setEmail('');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'Could not start the reset process. Please try again.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 3,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                Forgot password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Enter your sign-in email. If an account exists, we’ll email a reset link.
              </Typography>
            </Box>

            {info ? <Alert severity="success">{info}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                type="email"
                label="Email address"
                margin="normal"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                inputProps={{ inputMode: 'email' }}
                helperText="Use the same email you use to log in."
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={busy || !emailValue}
                sx={{
                  mt: 1.5,
                  borderRadius: 2,
                  py: 1.2,
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                {busy ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <span>Sending…</span>
                  </Stack>
                ) : (
                  'Send reset link'
                )}
              </Button>

              <Button
                component={RouterLink}
                to="/login"
                variant="text"
                fullWidth
                disabled={busy}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 800,
                }}
              >
                Back to login
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
